import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { businessManagerService } from 'app/service/business-manager'
import {
  FBAssignAssetResult,
  FBPersonAccess,
  FBSystemUserField,
  FBSystemUserOption,
  FBSystemUserOptions,
} from 'app/service/business-manager/BusinessManager.types'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import BmAvatar from './BmAvatar'
import { ConnectedPerson } from './FbAssignableField'

// One side of the business as the panel has it right now (possibly unsaved)
export interface AlignableAsset {
  id: string
  name?: string | null
  systemUserId?: string | null
  businessId?: string
  businessName?: string
  businessPictureUri?: string
}

interface SystemUserAlignDialogProps {
  open: boolean
  adAccount: AlignableAsset
  page: AlignableAsset
  onAssigned: (field: FBSystemUserField, result: FBAssignAssetResult) => void
  onClose: () => void
  // DRI-253: the person currently connecting the business, if any
  connectedBy?: ConnectedPerson
  // DRI-253: connect both through the viewer's own Facebook login, offered only when no system user of ours
  // reaches both assets. The panel saves the viewer as the business's one authorizer on Save.
  onUsePerson?: (access: FBPersonAccess) => void
}

const FIELD_LABELS: Record<FBSystemUserField, string> = { ad_account: 'Ad Account', page: 'Page' }

// A named Business Manager reads as just its name; "Facebook Business Manager" is spelled out where the copy
// talks about them in general, so it's never mistaken for something in Marketing Milk's own admin.
const bmLabel = (name?: string, id?: string) =>
  `${name ?? 'Unknown Facebook Business Manager'}${id ? ` (${id})` : ''}`

// DRI-442. Publishing creates ad sets and ads with the Ad Account's system user, and Facebook requires that
// system user to have Advertise access on the Page, so the two fields belong on one system user. This dialog:
//  - recommends one of our system users that can reach both and moves both fields onto it in one click
//    ("Use for both"), granting with the viewer's Facebook login only where it's missing
//  - lets an admin choose the system user (and so the Business Manager) used for each field by hand
//  - when no system user can reach both, explains the share that has to happen first, with both Business
//    Managers named, and offers the viewer's own Facebook login for both as a fallback (DRI-253) when it
//    holds the access needed on both
// Like every other field change in the panel, nothing is saved to the business until Save.
const SystemUserAlignDialog = (props: SystemUserAlignDialogProps) => {
  const { open, adAccount, page, onAssigned, onClose, connectedBy, onUsePerson } = props
  const [personAccess, setPersonAccess] = useState<FBPersonAccess | null | undefined>()
  const [options, setOptions] = useState<FBSystemUserOptions | undefined>()
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | undefined>()
  const [applying, setApplying] = useState(false)
  const [applyError, setApplyError] = useState<string | undefined>()
  const [selection, setSelection] = useState<Partial<Record<FBSystemUserField, string>>>({})

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setLoadError(undefined)
    setApplyError(undefined)
    businessManagerService
      .getFBSystemUserOptions({
        adAccountId: adAccount.id,
        pageId: page.id,
        adSystemUserId: adAccount.systemUserId ?? undefined,
        pageSystemUserId: page.systemUserId ?? undefined,
      })
      .then(result => {
        if (cancelled) return
        setOptions(result)
        setSelection({
          ad_account: result.recommendedSystemUserId ?? adAccount.systemUserId ?? undefined,
          page: result.recommendedSystemUserId ?? page.systemUserId ?? undefined,
        })
      })
      .catch((e: any) => {
        if (!cancelled) setLoadError(e?.message ?? 'Failed to load system users for these assets.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // only refetch when the dialog opens or the assets themselves change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, adAccount.id, page.id])

  // DRI-253: only once no system user of ours can hold both is the viewer's own login worth asking about
  const noRecommendation = !!options && !options.recommendedSystemUserId
  useEffect(() => {
    if (!open || !noRecommendation || !onUsePerson) return
    let cancelled = false
    setPersonAccess(undefined)
    businessManagerService
      .getFBPersonAccess({ adAccountId: adAccount.id, pageId: page.id })
      .then(result => {
        if (!cancelled) setPersonAccess(result)
      })
      .catch(() => {
        if (!cancelled) setPersonAccess(null)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, noRecommendation, adAccount.id, page.id])

  const optionsFor = (field: FBSystemUserField) =>
    (field === 'ad_account' ? options?.adAccount : options?.page) ?? []
  const assetFor = (field: FBSystemUserField) => (field === 'ad_account' ? adAccount : page)

  // our BM table can miss a name the panel already has from the business load
  const bmName = (o: FBSystemUserOption) =>
    o.fbBusinessName ??
    [adAccount, page].find(asset => asset.businessId === o.fbBusinessId)?.businessName
  // the system user id only tells options apart when two of them sit in the same Business Manager
  const optionLabel = (field: FBSystemUserField, o: FBSystemUserOption) => {
    const sharesBM =
      optionsFor(field).filter(other => other.fbBusinessId === o.fbBusinessId).length > 1
    return `${bmLabel(bmName(o), o.fbBusinessId)}${
      sharesBM ? ` · system user ${o.systemUserId}` : ''
    }${o.current ? ' · current' : ''}${o.ready ? ' · has access' : ' · needs a grant'}`
  }

  const split =
    !connectedBy &&
    !!adAccount.systemUserId &&
    !!page.systemUserId &&
    adAccount.systemUserId !== page.systemUserId
  const recommended = options?.recommendedSystemUserId
  const recommendedOption = (field: FBSystemUserField) =>
    optionsFor(field).find(o => o.systemUserId === recommended)

  // a field needs a call when it moves to another system user, or stays but is missing tasks there. On a person,
  // every field does: choosing a system user is what replaces the personal login.
  const fieldsToApply = (target: Partial<Record<FBSystemUserField, string>>) =>
    (['ad_account', 'page'] as FBSystemUserField[]).filter(field => {
      const systemUserId = target[field]
      if (!systemUserId) return false
      const option = optionsFor(field).find(o => o.systemUserId === systemUserId)
      return !!connectedBy || systemUserId !== assetFor(field).systemUserId || !option?.ready
    })

  const apply = async (target: Partial<Record<FBSystemUserField, string>>) => {
    setApplying(true)
    setApplyError(undefined)
    try {
      for (const field of fieldsToApply(target)) {
        try {
          const result = await businessManagerService.assignFacebookAssetToSystemUser({
            field,
            assetId: assetFor(field).id,
            systemUserId: target[field]!,
          })
          if (result.warning) toast.warn(result.warning.message)
          onAssigned(field, result)
        } catch (e: any) {
          throw new Error(
            `${FIELD_LABELS[field]}: ${e?.message ?? 'Failed to assign this system user.'}`
          )
        }
      }
      toast.success(
        target.ad_account && target.ad_account === target.page
          ? 'Ad Account and Page are now on one system user. Click Save to keep this change.'
          : 'System user updated. Click Save to keep this change.'
      )
      onClose()
    } catch (e: any) {
      setApplyError(e?.message)
    } finally {
      setApplying(false)
    }
  }

  const renderShareGuidance = () => (
    <Alert severity="warning" className="mb-3">
      <Typography variant="subtitle2" gutterBottom>
        No system user of ours can reach both assets yet, so one asset has to be shared first.
      </Typography>
      <ol className="pl-3 mb-2">
        <li>
          Share the Ad Account <strong>{adAccount.name ?? adAccount.id}</strong> ({adAccount.id})
          into the Page's Facebook Business Manager,{' '}
          <strong>{bmLabel(page.businessName, page.businessId)}</strong>, or share the Page{' '}
          <strong>{page.name ?? page.id}</strong> ({page.id}) into the Ad Account's,{' '}
          <strong>{bmLabel(adAccount.businessName, adAccount.businessId)}</strong>.
        </li>
        <li>
          In the Facebook Business Manager that owns the asset: Business Settings &rarr; Accounts
          &rarr; Ad Accounts (or Pages) &rarr; select it &rarr; Assign partners, and enter the other
          Facebook Business Manager's ID with full control (Manage).
        </li>
        <li>
          <strong>Another admin may need to approve.</strong> The share creates a request that a{' '}
          <em>different</em> admin of the sending (owning) Facebook Business Manager approves under
          Requests &rarr; Needs review. Each ad account is its own request.
        </li>
        <li>
          <strong>
            If the client's own Facebook Business Manager owns the asset, the client has to share
            it.
          </strong>{' '}
          It can't be done from our side.
        </li>
        <li>
          Once the share is approved, reopen this dialog, click <strong>Use for both</strong>, then
          Save.
        </li>
      </ol>
    </Alert>
  )

  // the same names the Error Center uses for these permissions
  const TASK_LABELS: Record<FBSystemUserField, Record<string, string>> = {
    ad_account: { ADVERTISE: 'Manage campaigns (ads)', ANALYZE: 'View performance' },
    page: { ADVERTISE: 'Ads', ANALYZE: 'Insights' },
  }
  const missingFor = (
    field: FBSystemUserField,
    access?: { tasks: string[] | null; ready: boolean }
  ) =>
    !access || access.ready
      ? []
      : ['ADVERTISE', 'ANALYZE']
          .filter(t => !access.tasks?.includes(t))
          .map(t => TASK_LABELS[field][t])
  const renderPersonFallback = () => {
    if (!onUsePerson) return null
    const ready = !!personAccess?.ready
    const unknown =
      personAccess === null ||
      personAccess?.adAccount?.tasks === null ||
      personAccess?.page?.tasks === null
    const adMissing = missingFor('ad_account', personAccess?.adAccount)
    const pageMissing = missingFor('page', personAccess?.page)
    const missing = [
      ...(adMissing.length ? [`${adMissing.join(' and ')} on the Ad Account`] : []),
      ...(pageMissing.length ? [`${pageMissing.join(' and ')} on the Page`] : []),
    ]
    return (
      <Alert
        severity="info"
        className="mb-3"
        action={
          ready ? (
            <Button
              color="primary"
              variant="contained"
              size="small"
              style={{ whiteSpace: 'nowrap' }}
              disabled={applying}
              onClick={() => {
                onUsePerson(personAccess!)
                toast.success(
                  'Connected with your Facebook login for both. Click Save to keep this change.'
                )
                onClose()
              }}
            >
              Use my login for both
            </Button>
          ) : undefined
        }
      >
        <Typography variant="subtitle2" gutterBottom>
          Can't get it shared? Use your own Facebook login for both
        </Typography>
        <div className="mb-1">
          The business then runs on your login: if you leave or your login expires, it stops working
          until someone reconnects it. Pick a system user again once the share is done.
        </div>
        {personAccess === undefined ? (
          <span className="d-flex align-items-center" style={{ gap: 8 }}>
            <CircularProgress size={14} /> Checking your access...
          </span>
        ) : ready ? (
          <strong>
            Your login has Manage campaigns (ads) and View performance on the Ad Account, and Ads
            and Insights on the Page.
            {personAccess?.page && !personAccess.page.leads
              ? " It doesn't have Leads access on the Page, so leads won't sync."
              : ''}
          </strong>
        ) : unknown ? (
          <strong>Couldn't check your access on Facebook. Reopen this dialog to try again.</strong>
        ) : (
          <strong>Your login is missing {missing.join(', and ')}.</strong>
        )}
      </Alert>
    )
  }

  // DRI-253: on a person, a system user for just one field would leave the other with no identity at all
  const needsBoth = !!connectedBy && !(selection.ad_account && selection.page)

  const recommendedAd = recommendedOption('ad_account')
  const recommendedPage = recommendedOption('page')
  const recommendedNeedsGrant =
    !!recommendedAd && !!recommendedPage && !(recommendedAd.ready && recommendedPage.ready)

  return (
    <Dialog open={open} onClose={applying ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Ad Account and Page system users</DialogTitle>
      <DialogContent>
        <p>
          Ads are created by the <strong>Ad Account's</strong> system user, and Facebook requires
          that same system user to have Advertise access on the <strong>Page</strong>. When the two
          are on different system users, publishing from the Ad Builder fails with{' '}
          <em>"Insufficient Page Permission to Run Ads"</em>, so keep both on one system user.
        </p>
        <div className="d-flex mb-3" style={{ gap: 16 }}>
          {(['ad_account', 'page'] as FBSystemUserField[]).map(field => {
            const asset = assetFor(field)
            return (
              <div key={field} className="border rounded p-3" style={{ flex: 1, minWidth: 0 }}>
                <Typography variant="overline" color="textSecondary" style={{ lineHeight: 1.2 }}>
                  {FIELD_LABELS[field]}
                </Typography>
                <Typography variant="subtitle1" style={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                  {asset.name ?? asset.id}
                </Typography>
                <div className="d-flex align-items-center mt-1" style={{ gap: 6 }}>
                  {connectedBy ? (
                    <BmAvatar
                      name={connectedBy.name}
                      pictureUri={connectedBy.pictureUri}
                      size={18}
                    />
                  ) : (
                    <BmAvatar
                      name={asset.businessName}
                      pictureUri={asset.businessPictureUri}
                      size={18}
                    />
                  )}
                  <Typography variant="caption" color="textSecondary">
                    {connectedBy
                      ? `Connected by: ${connectedBy.name} (personal login)`
                      : `System user: ${bmLabel(asset.businessName, asset.businessId)}`}
                  </Typography>
                </div>
              </div>
            )
          })}
        </div>

        {loading && (
          <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
            <CircularProgress size={16} /> Checking which of our system users can reach these
            assets...
          </div>
        )}
        {loadError && (
          <Alert severity="error" className="mb-3">
            {loadError}
          </Alert>
        )}

        {options && !loading && (
          <>
            {recommended && recommendedAd ? (
              <Alert
                severity={split ? 'warning' : 'success'}
                className="mb-3"
                action={
                  fieldsToApply({ ad_account: recommended, page: recommended }).length > 0 ? (
                    <Button
                      color="primary"
                      variant="contained"
                      size="small"
                      style={{ whiteSpace: 'nowrap' }}
                      disabled={applying}
                      onClick={() => apply({ ad_account: recommended, page: recommended })}
                    >
                      {applying ? 'Assigning...' : 'Use for both'}
                    </Button>
                  ) : undefined
                }
              >
                {fieldsToApply({ ad_account: recommended, page: recommended }).length > 0 ? (
                  <>
                    Our system user in{' '}
                    <strong>{bmLabel(bmName(recommendedAd), recommendedAd.fbBusinessId)}</strong>{' '}
                    can hold both.{' '}
                    {recommendedNeedsGrant
                      ? `It still needs access granted with your Facebook login, so you must be an admin of that Facebook Business Manager.`
                      : 'It already has access to both, so no grant is needed.'}
                  </>
                ) : (
                  <>Both are on one system user with the access they need. Nothing to fix.</>
                )}
              </Alert>
            ) : (
              <>
                {renderShareGuidance()}
                {renderPersonFallback()}
              </>
            )}
            {connectedBy && recommended && (
              <Typography variant="body2" className="mb-2">
                Using a system user replaces {connectedBy.name}'s personal login.
              </Typography>
            )}

            <Typography variant="subtitle2" className="mt-3 mb-2">
              Or choose the Facebook Business Manager used for each field
            </Typography>
            {(['ad_account', 'page'] as FBSystemUserField[]).map(field => (
              <TextField
                key={field}
                select
                fullWidth
                variant="outlined"
                size="small"
                className="mb-2"
                label={FIELD_LABELS[field]}
                value={selection[field] ?? ''}
                disabled={applying || !optionsFor(field).length}
                helperText={
                  optionsFor(field).length
                    ? undefined
                    : `None of our Facebook Business Managers have this ${FIELD_LABELS[field]}.`
                }
                onChange={e => setSelection(prev => ({ ...prev, [field]: e.target.value }))}
              >
                {optionsFor(field).map(o => (
                  <MenuItem key={o.systemUserId} value={o.systemUserId}>
                    {optionLabel(field, o)}
                  </MenuItem>
                ))}
              </TextField>
            ))}
            {needsBoth && (
              <Typography variant="body2" style={{ color: '#7a5b00' }}>
                Choose a system user for both: a business on {connectedBy!.name}'s personal login
                moves to system users for the Ad Account and Page together, never one of them.
              </Typography>
            )}
            {selection.ad_account && selection.page && selection.ad_account !== selection.page && (
              <Typography variant="body2" style={{ color: '#7a5b00' }}>
                These use different system users, so publishing will fail unless the Ad Account's
                one also has Advertise access on the Page.
              </Typography>
            )}
          </>
        )}

        {applyError && (
          <Alert severity="error" className="mt-3">
            {applyError}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={applying}>
          Close
        </Button>
        <Button
          color="primary"
          variant="outlined"
          disabled={applying || !options || needsBoth || fieldsToApply(selection).length === 0}
          onClick={() => apply(selection)}
        >
          {applying ? 'Assigning...' : 'Apply selection'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SystemUserAlignDialog
